import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Clock, Users, Zap, Shield, CheckCircle, AlertTriangle,
  Play, Pause, RotateCcw, ChevronRight, ExternalLink, FileText,
  Key, Laptop, Mail, MessageSquare, GitBranch, Database, Loader, Circle
} from 'lucide-react';
import { cases, actionBundles, timeline, getCaseById, getActionBundleById, domainCases } from '../data/caseData';
import { toast } from 'sonner';

const statusColors = {
  executing: 'bg-primary/20 text-primary border-primary/30',
  pending_approval: 'bg-slate-200 text-slate-700 border-slate-300',
  verifying: 'bg-slate-100 text-slate-600 border-slate-200',
  closed: 'bg-primary/10 text-primary border-primary/20',
  failed: 'bg-slate-900 text-white border-slate-900',
  open: 'bg-gray-100 text-gray-700 border-gray-200'
};

const statusLabels = {
  executing: 'Executing',
  pending_approval: 'Pending Approval',
  verifying: 'Verifying',
  closed: 'Completed',
  failed: 'Failed',
  open: 'Open'
};

const stateSteps = ['Signal', 'Cluster', 'Plan', 'Execute', 'Verify', 'Close'];

const ConnectorIcon = ({ name }) => {
  const icons = {
    okta: Key,
    entra_id: Key,
    google_workspace: Mail,
    intune: Laptop,
    slack: MessageSquare,
    github: GitBranch,
    figma: Zap,
    salesforce: Database
  };
  const Icon = icons[name] || Zap;
  return <Icon className="w-4 h-4" />;
};

const ActionStatusIcon = ({ status }) => {
  if (status === 'completed') return <CheckCircle className="w-4 h-4 text-primary" />;
  if (status === 'executing') return <Loader className="w-4 h-4 text-primary animate-spin" />;
  if (status === 'failed') return <AlertTriangle className="w-4 h-4 text-slate-900" />;
  return <Circle className="w-4 h-4 text-gray-300" />;
};

const TimelineIcon = ({ type }) => {
  const iconMap = {
    signal: Zap,
    case: FileText,
    cluster: Users,
    plan: FileText,
    policy: Shield,
    execute: Play,
    success: CheckCircle,
    progress: Loader,
    error: AlertTriangle
  };
  const Icon = iconMap[type] || Circle;
  const colorMap = {
    signal: 'text-slate-600',
    success: 'text-primary',
    progress: 'text-primary animate-spin',
    error: 'text-slate-900',
    policy: 'text-primary'
  };
  return <Icon className={`w-4 h-4 ${colorMap[type] || 'text-gray-400'}`} />;
};

export const CaseDetail = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('timeline');
  const [caseStatus, setCaseStatus] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isRollingBack, setIsRollingBack] = useState(false);

  const caseData = getCaseById(caseId);
  const bundle = caseData ? getActionBundleById(caseData.currentBundle) : null;

  // Initialize local status from case data
  React.useEffect(() => {
    if (caseData) {
      setCaseStatus(caseData.status);
    }
  }, [caseData]);

  const caseTimeline = timeline[caseId] || [
    // Generate a basic timeline for domain cases
    ...(caseData && !timeline[caseId] ? [
      { time: new Date(caseData.createdAt).toLocaleTimeString(), event: `Signal received from ${caseData.source?.system || 'System'}`, type: "signal", icon: "zap" },
      { time: new Date(caseData.createdAt).toLocaleTimeString(), event: "Case created", type: "case", icon: "file-plus" },
      { time: new Date(caseData.createdAt).toLocaleTimeString(), event: `Cluster formed: ${caseData.cluster?.agents?.join(', ') || 'agents'}`, type: "cluster", icon: "users" },
      { time: new Date(caseData.updatedAt).toLocaleTimeString(), event: `Action bundle planned (${bundle?.actions?.length || 1} actions)`, type: "plan", icon: "list" },
      ...(caseData.status === 'pending_approval' ? [{ time: new Date(caseData.updatedAt).toLocaleTimeString(), event: "Awaiting approval", type: "approval", icon: "clock" }] : []),
      ...(caseData.status === 'executing' ? [{ time: new Date(caseData.updatedAt).toLocaleTimeString(), event: "Execution in progress", type: "progress", icon: "loader" }] : [])
    ] : [])
  ];

  if (!caseData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-gray-900">Case not found</h2>
          <p className="text-sm text-gray-500 mt-1">The case you are looking for does not exist.</p>
          <button
            onClick={() => navigate('/control-tower')}
            className="mt-4 px-4 py-2 bg-[#4a70ff] text-white text-sm rounded-lg"
          >
            Back to Control Tower
          </button>
        </div>
      </div>
    );
  }

  // Determine current state step
  const getCurrentStep = () => {
    switch (caseData.status) {
      case 'open': return 0;
      case 'pending_approval': return 2;
      case 'executing': return 3;
      case 'verifying': return 4;
      case 'closed': return 5;
      default: return 0;
    }
  };
  const currentStep = getCurrentStep();

  return (
    <div className="min-h-screen bg-gray-50" data-testid="case-detail-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-4">
          <button
            onClick={() => navigate('/control-tower')}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-3"
            data-testid="back-to-control-tower"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Control Tower
          </button>

          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3">
                <span className={`px-2.5 py-1 text-xs font-medium rounded-lg border ${statusColors[caseData.status]}`}>
                  {statusLabels[caseData.status]}
                </span>
                <h1 className="text-xl font-semibold text-black">
                  {caseData.typeLabel}: {caseData.subject.name}
                </h1>
              </div>
              <p className="text-sm text-gray-500 mt-1">{caseData.intent.summary}</p>
              <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                <span>Case: {caseData.id}</span>
                <span>Created: {new Date(caseData.createdAt).toLocaleString()}</span>
                {caseData.sla && !caseData.sla.breached && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    SLA: {caseData.sla.remaining} remaining
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {caseData.status === 'pending_approval' && (
                <button
                  onClick={() => navigate(`/case/${caseId}/review`)}
                  className="px-4 py-2 bg-[#4a70ff] text-white text-sm font-medium rounded-lg hover:bg-[#3a5fd9] transition-colors"
                >
                  Review & Approve
                </button>
              )}
              {caseData.status === 'executing' && (
                <button
                  onClick={() => {
                    setIsPaused(!isPaused);
                    toast.success(isPaused ? 'Case execution resumed' : 'Case execution paused', {
                      description: `Case ${caseData.id} has been ${isPaused ? 'resumed' : 'paused'}`
                    });
                  }}
                  className={`px-3 py-2 border text-sm rounded-lg flex items-center gap-2 transition-colors ${isPaused
                      ? 'border-primary/30 text-primary bg-primary/10 hover:bg-primary/20'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  data-testid="pause-case-btn"
                >
                  {isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                  {isPaused ? 'Resume' : 'Pause'}
                </button>
              )}
              {caseData.status !== 'closed' && (
                <button
                  onClick={() => {
                    setIsRollingBack(true);
                    toast.info('Initiating rollback...', {
                      description: `Rolling back actions for case ${caseData.id}`
                    });
                    setTimeout(() => {
                      setIsRollingBack(false);
                      toast.success('Rollback completed', {
                        description: 'All reversible actions have been rolled back'
                      });
                    }, 2000);
                  }}
                  disabled={isRollingBack}
                  className="px-3 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
                  data-testid="rollback-case-btn"
                >
                  <RotateCcw className={`w-4 h-4 ${isRollingBack ? 'animate-spin' : ''}`} />
                  {isRollingBack ? 'Rolling back...' : 'Rollback'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        {/* State Machine */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6" data-testid="case-state-machine">
          <h3 className="text-sm font-semibold text-black mb-4">State Machine</h3>
          <div className="flex items-center justify-between">
            {stateSteps.map((step, idx) => (
              <React.Fragment key={step}>
                <div className="flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${idx < currentStep ? 'bg-primary text-white' :
                      idx === currentStep ? 'bg-primary text-white' :
                        'bg-gray-100 text-gray-400'
                    }`}>
                    {idx < currentStep ? <CheckCircle className="w-4 h-4" /> : idx + 1}
                  </div>
                  <span className={`text-xs mt-1.5 ${idx <= currentStep ? 'text-gray-700 font-medium' : 'text-gray-400'
                    }`}>{step}</span>
                </div>
                {idx < stateSteps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 ${idx < currentStep ? 'bg-primary' : 'bg-gray-200'
                    }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tabs */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="border-b border-gray-200">
                <div className="flex">
                  {['timeline', 'actions', 'evidence'].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`px-4 py-3 text-sm font-medium capitalize ${activeTab === tab
                          ? 'text-[#4a70ff] border-b-2 border-[#4a70ff]'
                          : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
              </div>

              <div className="p-4">
                {activeTab === 'timeline' && (
                  <div className="space-y-0" data-testid="case-timeline">
                    {caseTimeline.map((event, idx) => (
                      <div key={idx} className="flex gap-3 pb-4">
                        <div className="flex flex-col items-center">
                          <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center">
                            <TimelineIcon type={event.type} />
                          </div>
                          {idx < caseTimeline.length - 1 && (
                            <div className="w-0.5 flex-1 bg-gray-200 mt-2" />
                          )}
                        </div>
                        <div className="flex-1 pt-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">{event.event}</span>
                          </div>
                          <span className="text-xs text-gray-400">{event.time}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {activeTab === 'actions' && bundle && (
                  <div className="space-y-3" data-testid="action-bundle-list">
                    {bundle.actions.map((action) => (
                      <div
                        key={action.id}
                        className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors"
                      >
                        <ActionStatusIcon status={action.status} />
                        <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center">
                          <ConnectorIcon name={action.connector} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">{action.description}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded ${action.riskTier === 'low' ? 'bg-primary/10 text-primary' :
                                action.riskTier === 'medium' ? 'bg-slate-200 text-slate-700' :
                                  'bg-slate-900 text-white'
                              }`}>
                              {action.riskTier}
                            </span>
                          </div>
                          <div className="text-xs text-gray-400 mt-0.5">
                            {action.connector} / {action.operation}
                          </div>
                        </div>
                        {action.status === 'executing' && action.progress && (
                          <div className="w-16">
                            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-primary rounded-full transition-all"
                                style={{ width: `${action.progress}%` }}
                              />
                            </div>
                          </div>
                        )}
                        {action.status === 'completed' && action.completedAt && (
                          <span className="text-xs text-gray-400">
                            {new Date(action.completedAt).toLocaleTimeString()}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {activeTab === 'evidence' && (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">Evidence will be available after case completion</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Details */}
          <div className="space-y-6">
            {/* Cluster */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-black mb-3 flex items-center gap-2">
                <Users className="w-4 h-4 text-[#4a70ff]" />
                Cluster
              </h3>
              <div className="space-y-3">
                <div>
                  <span className="text-xs text-gray-400 uppercase tracking-wide">Agents</span>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {caseData.cluster.agents.map((agent) => (
                      <span key={agent} className="text-xs px-2 py-1 bg-[#4a70ff]/5 text-[#4a70ff] rounded-lg">
                        {agent.replace('_agent', '')}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <span className="text-xs text-gray-400 uppercase tracking-wide">Services</span>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {caseData.cluster.services.map((service) => (
                      <span key={service} className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-lg">
                        {service}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <span className="text-xs text-gray-400 uppercase tracking-wide">Dependencies</span>
                  <div className="space-y-1 mt-1">
                    {caseData.cluster.dependencies.map((dep, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-xs">
                        {dep.status === 'met' ? (
                          <CheckCircle className="w-3 h-3 text-primary" />
                        ) : (
                          <Clock className="w-3 h-3 text-slate-500" />
                        )}
                        <span className="text-gray-600">{dep.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Policy Checks */}
            {bundle && (
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <h3 className="text-sm font-semibold text-black mb-3 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-[#4a70ff]" />
                  Policy Checks
                </h3>
                <div className="space-y-2">
                  {bundle.policyChecks.map((check, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      {check.result === 'pass' ? (
                        <CheckCircle className="w-4 h-4 text-primary mt-0.5" />
                      ) : check.result === 'pending' ? (
                        <Clock className="w-4 h-4 text-slate-500 mt-0.5" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 text-slate-900 mt-0.5" />
                      )}
                      <div>
                        <span className="text-sm text-gray-900">{check.policy}</span>
                        <p className="text-xs text-gray-400">{check.message}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Blast Radius */}
            {bundle && (
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <h3 className="text-sm font-semibold text-black mb-3">Blast Radius</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-400 text-xs">Users</span>
                    <p className="font-medium text-gray-900">{bundle.blastRadius.usersAffected}</p>
                  </div>
                  <div>
                    <span className="text-gray-400 text-xs">Systems</span>
                    <p className="font-medium text-gray-900">{bundle.blastRadius.systemsAffected}</p>
                  </div>
                  <div>
                    <span className="text-gray-400 text-xs">Scope</span>
                    <p className="font-medium text-gray-900 capitalize">{bundle.blastRadius.dataScope.replace(/_/g, ' ')}</p>
                  </div>
                  <div>
                    <span className="text-gray-400 text-xs">Reversible</span>
                    <p className="font-medium text-gray-900 capitalize">{bundle.blastRadius.reversibility}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Subject Details */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-black mb-3">Subject</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Name</span>
                  <span className="text-gray-900">{caseData.subject.name}</span>
                </div>
                {caseData.subject.email && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Email</span>
                    <span className="text-gray-900">{caseData.subject.email}</span>
                  </div>
                )}
                {caseData.intent.parameters.department && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Department</span>
                    <span className="text-gray-900">{caseData.intent.parameters.department}</span>
                  </div>
                )}
                {caseData.intent.parameters.manager && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Manager</span>
                    <span className="text-gray-900">{caseData.intent.parameters.manager}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaseDetail;
